import {
  useEffect, useState, useCallback, useMemo, SyntheticEvent, ReactNode, Ref, ComponentRef, Key,
} from 'react';
import _ from 'lodash';
import ReactDragListView from 'react-drag-listview';
import { useTranslation } from 'react-i18next';
import {
  Empty, Grid, Table, TableProps,
} from 'antd';
import { IconSvg } from 'shared/icon';
import { StyledPaginationWrapper } from 'components/styledComponents/pagination/Pagination.styled';
import { LocalStorageService } from 'services';
import { useFullscreen } from 'hooks';
import { getResponsiveIconSize, getTableWidthSettings } from 'utils/styledHelpers';
import { StyledTable } from 'components/styledComponents/table/Table.styled';
import { AppConstants } from 'constants/index';
import { StyledRowExpandIcon } from 'shared/icon/Icon.styled';
import { getRandomNumber } from 'utils/utils';
import { AppColumn, AppColumns } from 'types/table';
import {
  PageSizeKeys, ReportColumn, TableKeys, UserConfig, UserConfigs,
} from 'app/userPreferences/types';
import { ColumnType } from 'antd/es/table';
import AdminTableSkeleton from './AdminTableSkeleton';
import ResizeableTitle from './ResizeableTitle';

const dragRow = {
  title: '',
  dataIndex: '',
  key: 'drag',
  width: 50,
  disabled: true,
  className: 'drag-row-handle',
  render: () => <IconSvg icon="DragIcon" width="1.71rem" height="1.71rem" color="" />,
};

const { actionMinWidth, rowSelectionWidth } = getTableWidthSettings();

const actionCols = ['actions', 'action'];
const pageSizeOptions: number[] = [...AppConstants.pageSizeOptions];

const { useBreakpoint } = Grid;
const defaultRowClassName = <T, >(__: T, index: number): string => (index % 2 ? 'odd' : 'even');
const getDataIndexKey = <T, >(dataIndex: AppColumn<T>['dataIndex']) => (Array.isArray(dataIndex) ? dataIndex.join('.') : String(dataIndex));

type AntdOnChange<T> = NonNullable<TableProps<T>['onChange']>;
type PaginationArg<T> = Parameters<AntdOnChange<T>>[0];
type FiltersArg<T> = Parameters<AntdOnChange<T>>[1];
type SorterArg<T> = Parameters<AntdOnChange<T>>[2];
type Unpacked<T> = T extends (infer U)[] ? U : T;
export type SingleSorter<T> = Unpacked<SorterArg<T>>;
export type OnTableChange<T> = (
  pagination: PaginationArg<T>,
  filters: FiltersArg<T>,
  sorter: SingleSorter<T>
) => void;

type Expandable<T> = NonNullable<TableProps<T>['expandable']>;

export interface AdminTableProps<T extends object> extends Omit<TableProps<T>, 'pagination' | 'columns'| 'onChange'> {
  pagination?: TableProps<T>['pagination'] & { hasNext?: boolean }|null;
  columns: AppColumns<T>;
  data?: T[]|null;
  expandedRowKeysProp?: Key[];
  tableKey?: TableKeys;
  isResizable?: boolean;
  configColumns?: ReportColumn[];
  dragCols?: boolean;
  dragRows?: boolean;
  createOrEditUserConfig?: (data: UserConfig) => void;
  pageSize?: number;
  pageSizeKey?: PageSizeKeys;
  tblRef?: Ref<ComponentRef<typeof Table>>;
  summaryData?: Partial<T>[];
  saveUserConfigKey?: (data: UserConfig) => void;
  moveRow?: (from: number, to: number) => void;
  onChange?: OnTableChange<T>;
}
type ExpandIconProps<T extends object> = Parameters<NonNullable<NonNullable<AdminTableProps<T>['expandable']>['expandIcon']>>[0];

const AdminTable = <T extends object, >({
  columns,
  data = null,
  rowKey = 'id',
  pagination = false,
  rowClassName = defaultRowClassName,
  components,
  size = 'small',
  scroll,
  dragCols = false,
  dragRows = false,
  moveRow,
  onChange,
  expandedRowKeysProp,
  expandable,
  tableKey,
  tableLayout,
  footer,
  isResizable = false,
  sticky = false,
  configColumns: existColumns,
  pageSize: existPageSize,
  pageSizeKey = 'pageSize' as PageSizeKeys,
  tblRef,
  summaryData,
  rowSelection,
  createOrEditUserConfig,
  saveUserConfigKey,
  ...rest
}: AdminTableProps<T>) => {
  const screens = useBreakpoint();

  const isMobile = useMemo(() => !screens.md, [screens.md]);
  const iconSize = useMemo(() => getResponsiveIconSize(screens), [screens]);

  const [currentPage, setCurrentPage] = useState<number | undefined>(0);
  const { fullscreen } = useFullscreen();

  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[] | undefined>(expandedRowKeysProp);
  const [tableData, setTableData] = useState(data);
  const [isResizing, setIsResizing] = useState(false);
  const { t } = useTranslation();

  const isTableResizable = useMemo(() => !isMobile && isResizable && tableData, [isMobile, isResizable, tableData]);

  const pureColumns = useMemo(() => columns.filter(({ hidden }) => !hidden), [columns]);

  const getColWidth = useCallback(
    (el: AppColumn<T>, colWidthConfig: Record<string, string | number | undefined> = {}) => (isMobile && actionCols.includes(String(el.dataIndex)) && iconSize)
      || (el.setInitialWidth ? _.max([el.actionsCount ? el.actionsCount * iconSize : 0, actionMinWidth]) : colWidthConfig[String(el.dataIndex)] || el.width),
    [isMobile, iconSize],
  );

  const getTitle = useCallback((el: AppColumn<T>) => (isMobile && actionCols.includes(String(el.dataIndex)) && ' ') || (_.isString(el.title) ? t(el.title) : el.title), [isMobile, t]);

  const handleTableCols = useCallback(
    (cols: AppColumns<T>) => {
      let changedCols = cols;
      const colWidthConfig: Record<string, AppColumn<T>['width']> = {};

      if (tableKey && !_.isUndefined(existColumns)) {
        const sortedCols: AppColumn<T>['dataIndex'][] = [];
        const checkedCols: AppColumn<T>['dataIndex'][] = [];
        existColumns.forEach((el) => {
          sortedCols.push(getDataIndexKey(el.dataIndex));
          if (el.checked) {
            checkedCols.push(getDataIndexKey(el.dataIndex));
          }
          if (isTableResizable) {
            colWidthConfig[el.dataIndex] = el.width;
          }
        });
        if (dragCols) {
          changedCols = _.sortBy(cols, (column) => sortedCols.indexOf(getDataIndexKey(column.dataIndex)));
        }
        if (isTableResizable) {
          changedCols = changedCols.map((el) => (el.dataIndex ? { ...el, width: getColWidth(el, colWidthConfig) } : el));
        }
        changedCols = changedCols.filter(({ dataIndex }) => checkedCols.includes(dataIndex));
      }
      if (dragRows) {
        changedCols = [{ ...dragRow, fixed: changedCols[0]?.fixed }, ...changedCols];
      }
      return changedCols.map((el) => ({ ...el, fixed: (!isMobile || actionCols.includes(String(el.dataIndex))) && el.fixed, ...(!!el.dataIndex && { title: getTitle(el), width: getColWidth(el) }) }));
    },
    [dragRows, isTableResizable, existColumns, dragCols, tableKey, getColWidth, getTitle, isMobile],
  );

  const configColumns = useMemo(() => handleTableCols(pureColumns), [handleTableCols, pureColumns]);

  const [tableCols, setTableCols] = useState(configColumns);

  useEffect(() => {
    setTableCols(configColumns);
  }, [setTableCols, configColumns]);

  useEffect(() => {
    setExpandedRowKeys(expandedRowKeysProp);
  }, [expandedRowKeysProp]);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  // To handle the case - getting current page but data is not changing yet
  useEffect(() => {
    if (pagination && !_.isNil(pagination.hasNext) && !_.isEqual(tableData, data)) {
      setCurrentPage(pagination.current);
    }
  }, [data, tableData, pagination]);

  const pageSize = useMemo(() => existPageSize || AppConstants.pageSize, [existPageSize]);

  // Calculate total include currentPage
  const currentTotal = useMemo(
    () => (pagination && !_.isNil(pagination.hasNext) && currentPage ? (currentPage - 1) * pageSize + (data?.length || 0) + +pagination.hasNext : 0),
    [currentPage, pageSize, data?.length, pagination],
  );

  const saveCols = useCallback(
    (movedCols: AppColumns<T>, isLoading: boolean = false) => {
      if (tableKey) {
        let configValue = [];
        const existsConfig = existColumns;
        if (_.isUndefined(existsConfig)) {
          configValue = movedCols.map(({ dataIndex, width }) => ({
            dataIndex: String(dataIndex),
            checked: true,
            width,
          }));
          createOrEditUserConfig?.({
            key: tableKey,
            configValue,
            skipGlobalLoading: !isLoading,
          });
        } else {
          const checkedCols: Record<string, boolean> = {};
          existsConfig?.forEach(({ dataIndex, checked }) => {
            checkedCols[String(dataIndex)] = checked;
          });
          configValue = movedCols.map(({ dataIndex, width }) => ({
            dataIndex: String(dataIndex),
            checked: checkedCols[String(dataIndex)],
            width,
          }));
          createOrEditUserConfig?.({
            key: tableKey,
            configValue,
            skipGlobalLoading: !isLoading,
          });
        }
      }
    },
    [existColumns, tableKey, createOrEditUserConfig],
  );

  const getAllSyncedColumns = useCallback(() => {
    const initialConfig = existColumns || pureColumns;
    const currentWidths = tableCols.reduce<Record<string, AppColumn<T>['width']>>((acc, { dataIndex, width }) => {
      acc[String(dataIndex)] = width;
      return acc;
    }, {});

    return initialConfig.map((col) => ({
      ...col,
      width: currentWidths[String(col.dataIndex)] || col.width,
    }));
  }, [pureColumns, existColumns, tableCols]);

  const dragColProps = {
    onDragEnd(from: number, to: number) {
      // to define indexes by column name, and re-order all columns
      const newData = getAllSyncedColumns();
      const fromDataIndex = tableCols[from]?.dataIndex;
      const toDataIndex = tableCols[to]?.dataIndex;
      let fromIndex = 0;
      let toIndex = 1;

      newData.forEach((el, index) => {
        if (fromDataIndex === el.dataIndex) {
          fromIndex = index;
        } else if (toDataIndex === el.dataIndex) {
          toIndex = index;
        }
      });
      [newData[fromIndex], newData[toIndex]] = [newData[toIndex], newData[fromIndex]];

      saveCols(newData);
    },
    nodeSelector: 'th:not([class*="ant-table-cell-fix"]), th.ant-table-cell-resizing:not([draggable="true"])',
    ignoreSelector: 'th.ant-table-row-expand-icon-cell, th.ant-table-selection-column, .ant-table-expanded-row-fixed th, th.ant-table-cell-resizing',
    lineClassName: 'dragging-col-custom',
  };

  const dragRowProps = {
    onDragEnd(fromIndex: number, toIndex: number) {
      // when scroll exists measure row adding at the top of table
      const from = scroll && Object.keys(scroll).length ? fromIndex - 1 : fromIndex;
      const to = scroll && Object.keys(scroll).length ? toIndex - 1 : toIndex;

      const newData = tableData ? [...tableData] : [];
      const item = newData.splice(from, 1)[0];
      newData.splice(to, 0, item);
      setTableData(newData);
      moveRow?.(from, to);
    },
    ignoreSelector: 'tr.ant-table-expanded-row',
    nodeSelector: 'tr.ant-table-row',
    handleSelector: 'td.drag-row-handle',
    lineClassName: 'dragging-row-custom',
  };

  const dragProps = dragCols && tableKey ? { ...dragColProps } : { ...dragRowProps };

  const onExpand: Expandable<T>['onExpand'] = (expanded, record) => {
    if (!window?.getSelection()?.toString().length) {
      const expandedRowKey = (_.isFunction(rowKey)
        ? rowKey(record)
        : record[rowKey as keyof T]) as string | number | undefined;

      if (!_.isNil(expandedRowKey)) {
        setExpandedRowKeys((prev) => {
          const currentKeys = prev ?? [];

          return currentKeys.includes(expandedRowKey)
            ? currentKeys.filter((key) => key !== expandedRowKey)
            : [...currentKeys, expandedRowKey];
        });
      }
    }
    expandable?.onExpand?.(expanded, record);
  };

  const onTableChange: TableProps<T>['onChange'] = (pg, filter, sorter) => {
    const isPageSizeChanged = pg.pageSize && pageSize !== pg.pageSize;
    if (isPageSizeChanged && pg.pageSize) {
      const userConfigs: UserConfigs = (LocalStorageService.get('configs') || {}) as UserConfigs;
      userConfigs[pageSizeKey] = pg.pageSize;
      LocalStorageService.set('configs', userConfigs);
      saveUserConfigKey?.({ key: pageSizeKey, configValue: pg.pageSize });
      createOrEditUserConfig?.({ key: pageSizeKey, configValue: pg.pageSize });
    }
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    onChange?.(pg, filter, singleSorter);
  };

  const handleResize = useCallback(
    (index: number) => (e: SyntheticEvent, { size: { width } }: { size: { width: number } }) => {
      e.preventDefault();
      setTableCols((prev) => {
        const nextColumns = [...prev];
        nextColumns[index] = {
          ...nextColumns[index],
          width,
        };
        return nextColumns;
      });
    },
    [],
  );

  const onResizeStart = useCallback(() => {
    setIsResizing(true);
    setTableCols((prev) => prev?.map((el) => ({ ...el, shouldCellUpdate: () => false })));
  }, []);

  const onResizeStop = useCallback(() => {
    if (isResizing) {
      const newCols = getAllSyncedColumns();
      saveCols(newCols, false);
      setIsResizing(false);
      setTableCols((prev) => prev?.map((el) => ({ ...el, shouldCellUpdate: () => true })));
    }
  }, [isResizing, saveCols, getAllSyncedColumns]);

  const cols: AppColumns<T> = useMemo(
    () => (isTableResizable
      ? tableCols.map((col, index) => (col.dataIndex && !actionCols.includes(col.dataIndex.toString())
        ? {
          ...col,
          onHeaderCell: (column: ColumnType<T>) => ({
            minWidth: column.minWidth,
            width: column.width,
            onResize: handleResize(index),
            onResizeStart,
            onResizeStop,
          }),
        }
        : col))
      : tableCols),
    [isTableResizable, tableCols, handleResize, onResizeStart, onResizeStop],
  );

  const renderSummary = () => {
    if (_.isNil(summaryData) || _.isEmpty(summaryData)) return null;

    return (
      <Table.Summary fixed>
        {summaryData?.map((row) => (
          <Table.Summary.Row key={getRandomNumber()}>
            {cols.map((col, index) => {
              const dataIndex = col.dataIndex as keyof T;
              const value = row[dataIndex];

              return (
                <Table.Summary.Cell index={index} key={col.key || String(col.dataIndex)}>
                  {col.renderSummary ? col.renderSummary(row) : (col.render?.(value, row as T, index) ?? value) as ReactNode}
                </Table.Summary.Cell>
              );
            })}
          </Table.Summary.Row>
        ))}
      </Table.Summary>
    );
  };

  // last page(s) | element delete handling
  useEffect(() => {
    if (pagination && tableData?.length === 0 && pagination?.current && pagination.current > 1) {
      const lastPage = Math.ceil((pagination.total || 0) / (pagination.pageSize || pageSize));
      if (pagination.current > lastPage) {
        onChange?.({ ...pagination, current: lastPage }, {}, {});
      }
    }
  }, [tableData, pagination, pageSize, onChange]);

  const renderTable = _.isNil(tableData) || (!tableData.length && pagination && pagination?.total) ? (
    <AdminTableSkeleton columns={cols} size={size} />
  ) : (
    <StyledPaginationWrapper $fullscreen={fullscreen}>
      <StyledTable
        ref={tblRef}
        columns={cols as TableProps['columns']}
        dataSource={tableData}
        rowClassName={rowClassName}
        rowKey={rowKey}
        components={
          isTableResizable
            ? {
              ...(components ?? {}),
              header: {
                ...components?.header,
                cell: ResizeableTitle,
              },
            }
            : components
        }
        pagination={
          pagination ? {
            showSizeChanger: !fullscreen ? {
              getPopupContainer: () => document.body,
            } : undefined,
            pageSize: existPageSize,
            showLessItems: true,
            defaultPageSize: AppConstants.pageSize,
            pageSizeOptions,
            showTotal: (total, range) => (_.isNil(pagination.hasNext) ? t('showTotal', { min: range[0], max: range[1], total }) : null),
            ...pagination,
            total: pagination.total ?? (!_.isNil(pagination.hasNext) && !!data?.length ? currentTotal : undefined),
          } : false
        }
        scroll={scroll}
        className={tableData?.length ? `${dragCols ? 'draggable-columns' : ''} ${dragRows ? 'draggable-rows' : ''}` : undefined}
        $fullscreen={fullscreen}
        onChange={onTableChange}
        tableLayout={tableLayout}
        footer={footer}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('noData')} />,
          triggerDesc: t('triggerDesc'),
          triggerAsc: t('triggerAsc'),
          expand: t('expandRow'),
          collapse: t('collapseRow'),
          selectionAll: t('selectionAllData'),
          cancelSort: t('cancelSort'),
        }}
        summary={renderSummary}
        expandable={
          tableData?.length
            ? {
              expandRowByClick: false,
              onExpand,
              expandedRowKeys,
              expandIcon: ({ record, expanded }: ExpandIconProps<T>) => expandable?.expandedRowRender
                    && (expanded ? (
                      <StyledRowExpandIcon
                        onClick={() => {
                          onExpand(expanded, record);
                        }}
                      >
                        <IconSvg icon="DropdownIcon" width="0.6rem" height="0.6rem" />
                      </StyledRowExpandIcon>
                    ) : (
                      <StyledRowExpandIcon
                        onClick={() => {
                          onExpand(expanded, record);
                        }}
                      >
                        <IconSvg icon="ArrowRightIcon" width="0.6rem" height="0.6rem" />
                      </StyledRowExpandIcon>
                    )),
              ...expandable,
            }
            : undefined
        }
        rowSelection={rowSelection ? { ...rowSelection, columnWidth: rowSelection?.columnWidth || rowSelectionWidth } : undefined}
        size={size}
        sticky={sticky && { offsetHeader: fullscreen ? 40 : 0 }}
        {...rest}
      />
    </StyledPaginationWrapper>
  );
  // drag & drop only if data exists
  if (!isMobile && tableData && tableData?.length && ((dragCols && tableKey) || dragRows)) {
    return <ReactDragListView {...dragProps}>{renderTable}</ReactDragListView>;
  }
  return renderTable;
};

export default AdminTable;
